// src/types/attribute.ts

/**
 * Attribute Types for PAP Frontend
 * Types for attribute definitions and API operations
 */

/**
 * Attribute category matching XACML categories
 */
export type AttributeCategory = 'SUBJECT' | 'RESOURCE' | 'ACTION' | 'ENVIRONMENT'

/**
 * Attribute data types matching XACML data types
 */
export type AttributeDataType =
  | 'STRING'
  | 'BOOLEAN'
  | 'INTEGER'
  | 'DOUBLE'
  | 'DATE'
  | 'TIME'
  | 'DATETIME'
  | 'URI'
  | 'ENUM'

/**
 * Attribute source types
 */
export type AttributeSourceType = 'LDAP' | 'DATABASE' | 'REST_API' | 'MANUAL'

/**
 * Validation rules for attribute values
 */
export interface ValidationRules {
  required?: boolean
  regex?: string
  minValue?: number
  maxValue?: number
  enumValues?: string[]
  minLength?: number
  maxLength?: number
  customMessage?: string
}

/**
 * Attribute definition from API
 */
export interface Attribute {
  id: string
  tenantId: string
  key: string
  displayName: string
  description: string | null
  category: AttributeCategory
  dataType: AttributeDataType
  isMultiValue: boolean
  isSystemManaged: boolean
  sourceType: AttributeSourceType
  sourceConfig: Record<string, unknown> | null
  validationEnabled: boolean
  validationRules: ValidationRules | null
  validFrom: string
  validTo: string | null
  createdAt: string
  updatedAt: string
  createdBy: string
}

/**
 * Request to create a new attribute
 */
export interface CreateAttributeRequest {
  key: string
  displayName: string
  description?: string
  category: AttributeCategory
  dataType: AttributeDataType
  isMultiValue?: boolean
  sourceType?: AttributeSourceType
  sourceConfig?: Record<string, unknown>
  validationEnabled?: boolean
  validationRules?: ValidationRules
}

/**
 * Request to update an attribute
 */
export interface UpdateAttributeRequest {
  displayName?: string
  description?: string
  dataType?: AttributeDataType
  isMultiValue?: boolean
  sourceType?: AttributeSourceType
  sourceConfig?: Record<string, unknown>
  validationEnabled?: boolean
  validationRules?: ValidationRules
}

/**
 * Query parameters for listing attributes
 */
export interface AttributeQueryParams {
  page?: number
  pageSize?: number
  search?: string
  category?: AttributeCategory
  dataType?: AttributeDataType
  sourceType?: AttributeSourceType
  isSystemManaged?: boolean
}

/**
 * Pagination metadata
 */
export interface AttributePagination {
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

/**
 * List response from API
 */
export interface AttributeListResponse {
  data: Attribute[]
  pagination: AttributePagination
}

/**
 * Display helpers
 */
export const CATEGORY_LABELS: Record<AttributeCategory, string> = {
  SUBJECT: 'Subject',
  RESOURCE: 'Resource',
  ACTION: 'Action',
  ENVIRONMENT: 'Environment',
}

export const DATA_TYPE_LABELS: Record<AttributeDataType, string> = {
  STRING: 'String',
  BOOLEAN: 'Boolean',
  INTEGER: 'Integer',
  DOUBLE: 'Double',
  DATE: 'Date',
  TIME: 'Time',
  DATETIME: 'DateTime',
  URI: 'URI',
  ENUM: 'Enum',
}

export const SOURCE_TYPE_LABELS: Record<AttributeSourceType, string> = {
  LDAP: 'LDAP',
  DATABASE: 'Database',
  REST_API: 'REST API',
  MANUAL: 'Manual',
}
